import React from 'react';
import styled from 'styled-components';
import { GhostButton } from '@ui/buttons';
import { Disclosure } from './Disclosure';
import { Body7 } from '@ui';
import { DateWindow } from '@features/scoreBackfill/DateWindow';
import { describeWindow, isNarrowable, utcDayStart, windowPresetsFor } from './knowableWindow';
import { formatUtcDate } from './format';

// Sized to sit level with the bar's text input and selects rather than styled as a link: it is one
// of the filters, and a control that looks like prose next to two boxes reads as a caption.
const TriggerButton = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};

  &:focus-visible {
    outline-offset: 1px;
  }
`;

// ONE ELEMENT, two layouts.
//
// This used to return a fragment of two siblings, because the panel is given a full-width flex basis
// so it wraps onto its own line of the bar — which only works if the trigger and the panel are both
// direct flex items of that bar. A fragment achieved it and made the component unmovable: reparent
// it into a popover, a grid cell or a drawer and `flex-basis: 100%` does nothing, so the panel
// renders inline beside the trigger and DateWindow overflows.
//
// `display: contents` on the wrapper is what buys one element without changing a pixel: the wrapper
// generates no box, so its children are still the bar's own flex items. `layout="block"` turns that
// off for a caller that is not a flex row, and is the whole cost of moving this control.
const Wrapper = styled.div`
  ${({ $layout }) => ($layout === 'bar' ? 'display: contents;' : '')}
`;

// Disclosure takes `panelAs` as an ELEMENT TYPE, so the layout travels as a PROP rather than by
// building a bound component here. A component built inline — even inside a useMemo — is a new type
// every time that memo drops, and React unmounts the old tree and mounts a fresh one for a type it
// has not seen before: the panel's DateWindow would lose everything it was holding, including a
// half-picked window.
const Panel = styled.div`
  ${({ $layout }) => ($layout === 'bar' ? 'flex-basis: 100%;' : '')}
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
`;

// Frozen per layout so the panel's props are a stable reference too — a fresh object each render
// would re-render the panel for a layout that never changed.
const PANEL_PROPS = { bar: { $layout: 'bar' }, block: { $layout: 'block' } };

// Narrowing the Knowable at (UTC) column over rows that are already loaded — no request, unlike the
// knowable window on the query form, which is what decides which rows exist in the first place. The
// two are deliberately unconnected: widening needs rows the client doesn't have, so this control can
// only ever subtract from what the last read returned.
//
// Every bound it can produce is inside the loaded span, and every preset it offers would actually
// change the table (see windowPresetsFor) — a filter able to select nothing, or to select everything
// twice, is a control that has to be tried before it can be understood.
//
// `span` arrives already computed rather than being derived from the rows here: it describes the
// READ, not the current view of it, and the table's summary memo is where the other whole-response
// scans live for exactly that reason — recomputing it on every keystroke in the text filter is the
// difference between a bar that types and one that stalls.
export const KnowableWindowFilter = ({ filters, onFilterChange, span, layout = 'bar' }) => {
  const presets = React.useMemo(() => windowPresetsFor(span), [span]);
  // Open on mount only if a window is already set — that state has to be visible rather than merely
  // summarised. Otherwise closed, which is the usual case and the one the bar's height is for.
  const [isOpen, setIsOpen] = React.useState(() => !!(filters.knowableFrom || filters.knowableTo));

  // The visibility rule lives HERE now, not in the caller. TableControls gated on
  // isNarrowable(knowableSpan) while this gated only on `!span`, so the two disagreed about when
  // the control exists — and rendered outside that caller, the weaker guard let it appear for
  // single-day results where every preset is a no-op.
  if (!isNarrowable(span)) return null;

  return (
    <Wrapper $layout={layout}>
      <Disclosure
        isOpen={isOpen}
        onToggle={() => setIsOpen((prev) => !prev)}
        triggerAs={TriggerButton}
        panelAs={Panel}
        panelProps={PANEL_PROPS[layout] || PANEL_PROPS.block}
        summary={
          <>
            {/* "Knowable window", not "Knowable at": the column's own header is a sort button called
                Knowable at (UTC), and two buttons on one screen whose accessible names both start
                that way is an ambiguity for anyone driving this by name. It is also what the query
                form calls the same field. */}
            {/* Two siblings rather than a Body7 inside a Body7: BaseText renders its own span with
                its own colour rule, and nesting one in another has the inner element fighting the
                outer for it. The label and the state are separate things anyway. */}
            <Body7 isLowContrast>Knowable window</Body7>
            <Body7 bold>{describeWindow(filters)}</Body7>
          </>
        }
      >
        <>
          <DateWindow
            fromDate={filters.knowableFrom}
            toDate={filters.knowableTo}
            onFromChange={(date) => onFilterChange({ knowableFrom: date })}
            onToChange={(date) => onFilterChange({ knowableTo: date })}
            minDate={span.from}
            maxDate={span.to}
            presets={presets}
            legend="Knowable at window"
          />
          {/* Says which zone the days are measured in and how far the rows in hand actually reach —
              both are why a preset here is anchored to the newest loaded row rather than to today,
              and neither is guessable from the chips. */}
          <Body7 isLowContrast>
            {`UTC days. These rows cover ${formatUtcDate(utcDayStart(span.from))} → ${formatUtcDate(
              utcDayStart(span.to)
            )} — narrowing only; re-run the query to widen.`}
          </Body7>
        </>
      </Disclosure>
    </Wrapper>
  );
};
